import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';
import {
  buildCountryTitleListParams,
  normalizeCountryTitleStatus,
  toCountryTitleApiPayload,
} from '../../../utils/country-title-catalog.utils';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Origen de fondos por país.
 * Endpoint: `/settings/countries/{countryId}/origin-funds`
 */
@Injectable({ providedIn: 'root' })
export class FundOriginService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private resource(countryId: string, id?: string | number): string {
    const baseUrl = `${this.apiUrl}/${countryId}/origin-funds`;
    return id !== undefined && id !== null ? `${baseUrl}/${id}` : baseUrl;
  }

  private mapItem(it: any): FundOrigin {
    return {
      id: it.id,
      name: it.name || it.title || '',
      detail: it.detail ?? false,
      status: normalizeCountryTitleStatus(it.status),
    };
  }

  getFundOrigins(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<FundOriginPageResponse> {
    const params = buildCountryTitleListParams({
      page,
      size,
      sort,
      direction,
      search,
      status,
    });
    return this.httpClient
      .get<ApiResponse<PageableData>>(this.resource(countryId), { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((it: any) => this.mapItem(it)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          } as FundOriginPageResponse;
        }),
      );
  }

  getFundOriginById(countryId: string, id: string | number) {
    return this.httpClient
      .get<ApiResponse<any>>(this.resource(countryId, id))
      .pipe(map((resp) => this.mapItem(resp.data)));
  }

  createFundOrigin(countryId: string, payload: any) {
    return this.httpClient
      .post<{ success: boolean; message: string }>(
        this.resource(countryId),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Origen de fondos creado' })));
  }

  updateFundOrigin(countryId: string, id: string | number, payload: any) {
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        this.resource(countryId, id),
        toCountryTitleApiPayload(payload),
      )
      .pipe(map((res) => ({ message: res.message || 'Origen de fondos actualizado' })));
  }

  deleteFundOrigin(countryId: string, id: string | number) {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(
        this.resource(countryId, id),
      )
      .pipe(map((res) => ({ message: res.message || 'Origen de fondos eliminado' })));
  }
}

export interface FundOrigin {
  id: string;
  name: string;
  status?: string;
  detail?: boolean;
}

export interface FundOriginPageResponse {
  items: FundOrigin[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

